import { useState, useEffect } from 'react';
import { 
  MdNotifications, 
  MdNotificationsNone, 
  MdCheckCircle, 
  MdAttachMoney, 
  MdAssignment, 
  MdDone 
} from 'react-icons/md';
import api from '../../services/api';
import { t } from '../../utils/i18n';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications').catch(() => ({ data: [] }));
      // Handle both array and object responses
      const data = response.data || [];
      const notificationsArray = Array.isArray(data) ? data : (data.notifications || []);
      // Normalize notification format: backend uses isRead, frontend expects read
      const normalizedNotifications = notificationsArray.map(notif => ({
        ...notif,
        read: notif.isRead !== undefined ? notif.isRead : notif.read,
        type: notif.type || 'default',
        message: notif.message || notif.title || 'Notification',
        createdAt: notif.createdAt || notif.created_at || new Date().toISOString()
      }));
      setNotifications(normalizedNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`).catch(() => {});
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_accepted':
        return <MdCheckCircle className="text-green-600 dark:text-green-400 text-2xl" />;
      case 'payment_received':
        return <MdAttachMoney className="text-green-600 dark:text-green-400 text-2xl" />;
      case 'job_posted':
        return <MdAssignment className="text-primary-600 dark:text-primary-400 text-2xl" />;
      case 'job_completed':
        return <MdDone className="text-primary-600 dark:text-primary-400 text-2xl" />;
      default:
        return <MdNotifications className="text-gray-600 dark:text-gray-400 text-2xl" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 max-w-4xl mx-auto p-4 lg:p-6 space-y-6">
        <div className="surface-panel flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary-600/10 flex items-center justify-center">
              <MdNotifications className="text-primary-600 dark:text-primary-400 text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('notifications.title') || 'Obaveštenja'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('notifications.subtitle') || 'Sve važne novosti na jednom mestu.'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-semibold">
              {unreadCount} {t('notifications.unread') || 'nepročitano'}
            </span>
          )}
        </div>

      {notifications.length === 0 ? (
        <div className="card text-center py-12">
          <MdNotificationsNone className="text-5xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {t('notifications.noNotifications') || 'No notifications'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`
                card p-4 cursor-pointer hover:shadow-md transition-all
                ${!notification.read ? 'border-l-4 border-primary-600 bg-primary-50 dark:bg-primary-900/10' : ''}
              `}
              onClick={() => !notification.read && markAsRead(notification.id)}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${!notification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                    {notification.message || notification.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {new Date(notification.createdAt).toLocaleString('sr-RS', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-primary-600 flex-shrink-0 mt-2"></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

